import {
    Box,
    Button,
    Alert,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import type { SaveActivityGroupResponse } from '../../../types/activity';
import type { ActivityFormData } from './types';
import ActivityFormItem from './ActivityFormItem';
import ActivityGroupSection from './ActivityGroupSection';
import type { GroupAudit } from './ActivityGroupSection';
import { useActivityGroupData } from './useActivityGroupData';
import { useActivityNavigation } from './useActivityNavigation';
import { useActivityGroupSubmit } from './useActivityGroupSubmit';

export interface ActivityGroupEditorProps {
    companyId: number;
    /** If set, the group is in edit mode (group.bid will be set) */
    groupBid?: number;
    /** Initial description for the group */
    initialDescription?: string;
    /** Initial activities to pre-populate (with bid set for existing ones) */
    initialActivities?: ActivityFormData[];
    /** Existing group's paid-by-client amount, when editing. Undefined in create mode. */
    initialPaidByClientAmount?: number;
    /** Existing group's paid-to-provider amount, when editing. Undefined in create mode. */
    initialPaidToProviderAmount?: number;
    /** Existing group's creator/updater info, when editing. Undefined in create mode. */
    audit?: GroupAudit;
    /** Overrides where the in-progress draft is parked. */
    storageKey?: string;
    /** Called after successful save. Receives the response. */
    onSuccess: (response: SaveActivityGroupResponse) => void;
    /** Called when user cancels */
    onCancel: () => void;
}

export default function ActivityGroupEditor({
    companyId,
    groupBid,
    initialDescription = '',
    initialActivities,
    initialPaidByClientAmount,
    initialPaidToProviderAmount,
    audit,
    storageKey: storageKeyOverride,
    onSuccess,
    onCancel,
}: ActivityGroupEditorProps) {
    const {
        activities,
        setActivities,
        groupDescription,
        setGroupDescription,
        clientTotal,
        providerTotal,
        paidByClientAmount,
        setPaidByClientAmount,
        paidToProviderAmount,
        setPaidToProviderAmount,
        activityTypes,
        operationTypesLoaded,
        loadError,
        balanceMap,
        storageKey,
        handleActivityChange,
        handleAddMore,
        handleCopyActivity,
        handleRemoveActivity,
        handleRemoveClient,
        handleRemoveProduct,
        handleRemoveProvider,
        handleRemoveStock,
        handleRemoveStockFrom,
        handleReset,
    } = useActivityGroupData({
        companyId,
        groupBid,
        initialDescription,
        initialActivities,
        initialPaidByClientAmount,
        initialPaidToProviderAmount,
        storageKey: storageKeyOverride,
    });

    const navigation = useActivityNavigation({ companyId, setActivities });

    const {
        loading,
        error,
        validationError,
        validationLoading,
        activityServerErrors,
        generalServerErrors,
        isFormValid,
        handleSubmit,
        clearActivityErrors,
    } = useActivityGroupSubmit({
        companyId,
        groupBid,
        activities,
        activityTypes,
        groupDescription,
        paidByClientAmount,
        paidToProviderAmount,
        operationTypesLoaded,
        storageKey,
        onSuccess,
    });

    const handleFieldChange = (feId: string, updater: (prev: ActivityFormData) => ActivityFormData) => {
        handleActivityChange(feId, updater);
        clearActivityErrors(feId);
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <ActivityGroupSection
                groupDescription={groupDescription}
                onGroupDescriptionChange={setGroupDescription}
                audit={audit}
                clientTotal={clientTotal}
                providerTotal={providerTotal}
                paidByClientAmount={paidByClientAmount}
                onPaidByClientAmountChange={setPaidByClientAmount}
                paidToProviderAmount={paidToProviderAmount}
                onPaidToProviderAmountChange={setPaidToProviderAmount}
            />

            {activities.map((activity, index) => (
                <ActivityFormItem
                    key={activity.feId}
                    activity={activity}
                    index={index}
                    activityTypes={activityTypes}
                    operationTypesLoaded={operationTypesLoaded}
                    showRemoveButton={activities.length > 1}
                    serverErrors={activityServerErrors[activity.feId]}
                    stockBalance={activity.productId && activity.stockId ? balanceMap[activity.productId]?.[activity.stockId]?.quantity : undefined}
                    stockFromBalance={activity.productId && activity.stockFromId ? balanceMap[activity.productId]?.[activity.stockFromId]?.quantity : undefined}
                    stockAverageUnitCost={activity.productId && activity.stockId ? balanceMap[activity.productId]?.[activity.stockId]?.averageUnitCost : undefined}
                    stockFromAverageUnitCost={activity.productId && activity.stockFromId ? balanceMap[activity.productId]?.[activity.stockFromId]?.averageUnitCost : undefined}
                    onNavigateToAvailability={navigation.handleNavigateToAvailability}
                    onUpdate={(updater) => handleFieldChange(activity.feId, updater)}
                    onCopy={() => handleCopyActivity(activity.feId)}
                    onRemove={() => handleRemoveActivity(activity.feId)}
                    onRemoveProduct={() => handleRemoveProduct(activity.feId)}
                    onRemoveClient={() => handleRemoveClient(activity.feId)}
                    onRemoveProvider={() => handleRemoveProvider(activity.feId)}
                    onRemoveStock={() => handleRemoveStock(activity.feId)}
                    onRemoveStockFrom={() => handleRemoveStockFrom(activity.feId)}
                    onOpenProductSelector={() => navigation.handleOpenProductSelector(activity.feId, undefined)}
                    onOpenClientSelector={() => navigation.handleOpenClientSelector(activity.feId, activity.clientId)}
                    onOpenProviderSelector={() => navigation.handleOpenProviderSelector(activity.feId, activity.providerId)}
                    onOpenStockSelector={() => navigation.handleOpenStockSelector(activity.feId, activity.stockId)}
                    onOpenStockFromSelector={() => navigation.handleOpenStockFromSelector(activity.feId, activity.stockFromId)}
                />
            ))}

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddMore}
                sx={{ mb: 3 }}
            >
                Додати ще одну активність
            </Button>

            {(loadError || error) && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {loadError || error}
                </Alert>
            )}

            {validationError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {validationError}
                </Alert>
            )}

            {!validationError && generalServerErrors.length > 0 && (
                generalServerErrors.map((msg, i) => (
                    <Alert key={i} severity="error" sx={{ mb: 2 }}>
                        {msg}
                    </Alert>
                ))
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button
                    color="inherit"
                    onClick={handleReset}
                    disabled={loading}
                >
                    Скинути
                </Button>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Скасувати
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || validationLoading || !!validationError || !isFormValid()}
                    >
                        {loading ? 'Збереження...' : validationLoading ? 'Перевірка...' : 'Зберегти'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
